import React, { FC } from 'react';
import styled from 'styled-components';
import { Loader } from '../../../../../atoms';

type Column = {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  render?: (row: any) => React.ReactNode;
};

type Props = {
  columns: Column[];
  rows: any[];
  emptyMessage?: string;
  onRowClick?: (row: any) => void;
  isLoading?: boolean;
};

const DashboardRowList: FC<Props> = ({
  columns,
  rows,
  emptyMessage = 'Nothing to show yet.',
  onRowClick,
  isLoading = false,
}) => {
  const gridTemplate = columns
    .map(c => c.width || '1fr')
    .join(' ');

  if (isLoading && !rows?.length) {
    return (
      <LoaderWrapper>
        <Loader size={32} thickness={3.5} />
        <LoaderLabel>Loading…</LoaderLabel>
      </LoaderWrapper>
    );
  }

  if (!rows?.length) {
    return <Empty>{emptyMessage}</Empty>;
  }

  return (
    <Wrapper>
      <HeaderRow style={{ gridTemplateColumns: gridTemplate }}>
        {columns.map(c => (
          <HeaderCell key={c.key} align={c.align || 'left'}>
            {c.label}
          </HeaderCell>
        ))}
      </HeaderRow>
      {rows.map((row, index) => (
        <Row
          // Project rows share the project `id` across its permits, so `id`
          // alone isn't unique — duplicate keys leave stale rows on paging.
          key={`${row.id ?? ''}:${row.permitId ?? ''}:${index}`}
          style={{ gridTemplateColumns: gridTemplate }}
          clickable={Boolean(onRowClick)}
          onClick={() => onRowClick && onRowClick(row)}
        >
          {columns.map(c => (
            <Cell key={c.key} align={c.align || 'left'}>
              {c.render ? c.render(row) : row[c.key]}
            </Cell>
          ))}
        </Row>
      ))}
    </Wrapper>
  );
};

export default DashboardRowList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: grid;
  padding: 12px 20px;
  background: var(--neutral-50);
  border-bottom: 1px solid var(--border);
  align-items: center;
  gap: 16px;

  @media only screen and (max-width: 540px) {
    display: none;
  }
`;

const HeaderCell = styled.div<{ align: string }>`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--neutral-500);
  text-align: ${({ align }) => align};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Row = styled.div<{ clickable: boolean }>`
  display: grid;
  padding: 14px 20px;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid var(--border);
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: background-color 0.12s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ clickable }) =>
      clickable ? 'var(--neutral-50)' : 'transparent'};
  }

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr !important;
    gap: 6px;
  }
`;

const Cell = styled.div<{ align: string }>`
  font-size: 0.875rem;
  color: var(--mainText);
  text-align: ${({ align }) => align};
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  line-height: 1.35;

  @media only screen and (max-width: 540px) {
    text-align: left;
  }
`;

const Empty = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--neutral-500);
`;

const LoaderLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.02em;
`;
